import { ApolloError, ServerError, gql } from '@apollo/client';
import { createContext, useContext, useEffect } from 'react';

import { Button } from 'component-lib/atoms/button/Button';
import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { useNotificationContext } from 'core/context/Notifications';
import { useOverrideRole } from 'core/molecules/feature-flag-manager/FeatureFlagManager';
import { AuthenticationUserFragment, RoleName, UserSegment, useGetInstruqtUserLazyQuery } from 'types/graphql';

import { useAuthTokenContext } from './AuthTokenContext';

export interface AuthUserContextType {
  /**
   * The backend user for the current auth token
   */
  instruqtUser: AuthenticationUserFragment;

  /**
   * Whether the user has a user profile with us
   */
  isAnonymous: boolean;

  /**
   * Whether this is a passwordless login
   */
  isSSO: boolean;
}

export const DEFAULT_VALUE: AuthUserContextType = {
  instruqtUser: {
    id: '',
    is_global_admin: false,
    profile: null,
    teams: [],
    segments: [],
  },
  isAnonymous: false,
  isSSO: false,
};

export const AuthUserContext = createContext<AuthUserContextType>(DEFAULT_VALUE);
export function useAuthUserContext() {
  return useContext(AuthUserContext);
}

export function AuthUserContextProvider({ children }: { children: React.ReactNode }) {
  const { tokenID, isSSO, logout } = useAuthTokenContext();
  const { notifications } = useNotificationContext();
  const [roleOverride] = useOverrideRole();

  const [getInstruqtUser, { data, error }] = useGetInstruqtUserLazyQuery({ fetchPolicy: 'network-only' });

  useEffect(() => {
    // Get a new user everytime tokenID changes
    if (tokenID) getInstruqtUser();
  }, [tokenID, getInstruqtUser]);

  const instruqtUser = data?.me;

  if (!instruqtUser) {
    const hasError = Boolean(error || notifications.filter(n => n.type === 'error').length);

    return (
      <div className="flex flex-col items-center gap-2 py-2">
        <LoadingImage className="!m-0" />

        {hasError && (
          <>
            {error && <ApolloErrorDisplay error={error} />}

            {/* Notifications only need handling if we fail to resolve a user, layout templates can render them themselves later */}
            {notifications.map(n => (
              <p className="text-error" key={n.id}>
                {n.title}: {n.message}
              </p>
            ))}

            <div className="flex items-center gap-1">
              <Button onClick={logout}>Logout</Button>
              <Button onClick={() => window.location.reload()}>Refresh</Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <AuthUserContext.Provider
      value={{
        instruqtUser: !roleOverride
          ? instruqtUser
          : {
              ...instruqtUser,
              is_global_admin: false,
              segments:
                roleOverride === RoleName.Member
                  ? [UserSegment.Member]
                  : [UserSegment.Member, UserSegment.ContentCreator],
              teams: instruqtUser.teams.map(t => ({ ...t, role: roleOverride })),
            },
        isAnonymous: !instruqtUser.profile?.email,
        isSSO: isSSO || false,
      }}
    >
      {children}
    </AuthUserContext.Provider>
  );
}

function ApolloErrorDisplay({ error: { message, networkError } }: { error: ApolloError }) {
  let networkMessage: string | undefined;

  if (networkError && isServerError(networkError)) {
    networkMessage =
      typeof networkError.result === 'string'
        ? networkError.result
        : (networkError.result.errors as { message: string }[]).map(e => e.message).join('\n');
  }

  return (
    <pre className="text-error text-center">
      AuthError: {message}
      {networkMessage && `\n${networkMessage}`}
    </pre>
  );
}

function isServerError(error: Error): error is ServerError {
  return error.name === 'ServerError';
}

gql`
  query GetInstruqtUser {
    me {
      ...AuthenticationUser
    }
  }

  fragment AuthenticationUser on User {
    id
    segments
    is_global_admin

    profile {
      email
      display_name
      avatar
      slug
    }

    teams {
      role

      node {
        id
        slug
        name

        features {
          hot_start
          invites
          statistics
          arcades
          webhooks
          track_reviews
          shared_hotstart_pools
        }
      }
    }

    ...AmplitudeUser
  }
`;
